import type { NotificationResponseItem } from '@/api/notification/notificationTypes';
import { defineStore } from 'pinia';
import {
  archiveNotificationApi,
  archiveReadNotificationsApi,
  fetchNotificationsApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
} from '@/api/notification/notificationApi';

export type NotificationCategory = 'task' | 'result';
export type NotificationTone = 'blue' | 'emerald' | 'amber' | 'rose';
export type NotificationEvent
  = | 'course_review_submitted'
    | 'course_review_approved'
    | 'course_review_rejected'
    | 'course_review_delisted'
    | 'visualization_review_submitted'
    | 'visualization_review_resubmitted'
    | 'visualization_review_approved'
    | 'visualization_review_rejected'
    | 'course_content_updated';

export interface NotificationPayload {
  actorName?: string;
  courseId?: number | string;
  courseName?: string;
  visualizationId?: number | string;
  resourceName?: string;
  reason?: string;
  reviewScope?: 'course' | 'platform';
}

export interface NotificationItem {
  id: number | string;
  category: NotificationCategory;
  event: NotificationEvent;
  payload: NotificationPayload;
  createdAt: string;
  isRead: boolean;
  resolvedAt?: string | null;
}

function mapPayload(payload: Record<string, unknown>): NotificationPayload {
  return {
    actorName: typeof payload.actor_name === 'string' ? payload.actor_name : undefined,
    courseId: typeof payload.course_id === 'string' || typeof payload.course_id === 'number' ? payload.course_id : undefined,
    courseName: typeof payload.course_name === 'string' ? payload.course_name : undefined,
    visualizationId: typeof payload.visualization_id === 'string' || typeof payload.visualization_id === 'number' ? payload.visualization_id : undefined,
    resourceName: typeof payload.resource_name === 'string' ? payload.resource_name : undefined,
    reason: typeof payload.reason === 'string' ? payload.reason : undefined,
    reviewScope: payload.review_scope === 'course' || payload.review_scope === 'platform' ? payload.review_scope : undefined,
  };
}

function mapNotification(item: NotificationResponseItem): NotificationItem | null {
  const supportedEvents: NotificationEvent[] = [
    'course_review_submitted',
    'course_review_approved',
    'course_review_rejected',
    'course_review_delisted',
    'visualization_review_submitted',
    'visualization_review_resubmitted',
    'visualization_review_approved',
    'visualization_review_rejected',
    'course_content_updated',
  ];
  if (!supportedEvents.includes(item.event_type as NotificationEvent))
    return null;
  return {
    id: item.id,
    category: item.category,
    event: item.event_type as NotificationEvent,
    payload: mapPayload(item.payload || {}),
    createdAt: item.created_at,
    isRead: item.is_read,
    resolvedAt: item.resolved_at,
  };
}

function minutesAgo(minutes: number) {
  return new Date(Date.now() - minutes * 60 * 1000).toISOString();
}

function adminPreviewItems(): NotificationItem[] {
  return [
    {
      id: 'admin-course-review-1',
      category: 'task',
      event: 'course_review_submitted',
      payload: { actorName: '李巧悦', courseName: '概率论与数理统计' },
      createdAt: minutesAgo(3),
      isRead: false,
    },
    {
      id: 'admin-visualization-review-1',
      category: 'task',
      event: 'visualization_review_submitted',
      payload: { actorName: '王同学', courseName: '概率论', resourceName: '中心极限定理演示', reviewScope: 'platform' },
      createdAt: minutesAgo(18),
      isRead: false,
    },
    {
      id: 'admin-visualization-review-2',
      category: 'task',
      event: 'visualization_review_resubmitted',
      payload: { actorName: '张同学', resourceName: '贝叶斯公式演示', reviewScope: 'platform' },
      createdAt: minutesAgo(75),
      isRead: false,
    },
  ];
}

function teacherPreviewItems(): NotificationItem[] {
  return [
    {
      id: 'teacher-course-result-1',
      category: 'result',
      event: 'course_review_rejected',
      payload: { courseName: '概率论与数理统计', reason: '请补充课程目标和适用人群。' },
      createdAt: minutesAgo(6),
      isRead: false,
    },
    {
      id: 'teacher-visualization-review-1',
      category: 'task',
      event: 'visualization_review_submitted',
      payload: { actorName: '王同学', courseName: '概率论', resourceName: '正态分布演示', reviewScope: 'course' },
      createdAt: minutesAgo(22),
      isRead: false,
    },
    {
      id: 'teacher-course-result-2',
      category: 'result',
      event: 'course_review_approved',
      payload: { courseName: '线性代数' },
      createdAt: minutesAgo(110),
      isRead: false,
    },
    {
      id: 'teacher-visualization-result-1',
      category: 'result',
      event: 'visualization_review_approved',
      payload: { resourceName: '随机变量模拟' },
      createdAt: minutesAgo(24 * 60),
      isRead: true,
    },
  ];
}

function studentPreviewItems(): NotificationItem[] {
  return [
    {
      id: 'student-visualization-result-1',
      category: 'result',
      event: 'visualization_review_rejected',
      payload: { resourceName: '正态分布演示', reason: '坐标轴缺少单位，请补充后重新提交。' },
      createdAt: minutesAgo(9),
      isRead: false,
    },
    {
      id: 'student-visualization-result-2',
      category: 'result',
      event: 'visualization_review_approved',
      payload: { resourceName: '中心极限定理演示' },
      createdAt: minutesAgo(95),
      isRead: false,
    },
    {
      id: 'student-course-result-1',
      category: 'result',
      event: 'course_content_updated',
      payload: { courseName: '概率论' },
      createdAt: minutesAgo(24 * 60),
      isRead: true,
    },
  ];
}

function previewItems(role: number, isAdmin: boolean) {
  const baseItems = role === 2
    ? teacherPreviewItems()
    : role === 1
      ? studentPreviewItems()
      : [];
  const items = isAdmin ? [...adminPreviewItems(), ...baseItems] : baseItems;
  return items.sort((first, second) => Date.parse(second.createdAt) - Date.parse(first.createdAt));
}

export function isNotificationPreviewEnabled(explicitPreview: unknown) {
  if (!import.meta.env.DEV)
    return false;
  return explicitPreview === '1'
    || (!localStorage.getItem('token') && [
      'probfun_local_teacher_preview',
      'probfun_local_admin_preview',
      'probfun_local_student_preview',
    ].some(key => sessionStorage.getItem(key) === '1'));
}

export const useNotificationStore = defineStore('notification-center', {
  state: () => ({
    items: [] as NotificationItem[],
    previewIdentity: '',
    loading: false,
    error: '',
    lastLoadedAt: 0,
    page: 0,
    hasMore: false,
    total: 0,
    currentCategory: undefined as NotificationCategory | undefined,
    currentUnreadOnly: false,
    counts: { all: 0, unread: 0, task: 0, result: 0 },
    requestSequence: 0,
  }),
  getters: {
    unreadCount: state => state.previewIdentity
      ? state.items.filter(item => !item.isRead && !item.resolvedAt).length
      : state.counts.unread,
    taskCount: state => state.previewIdentity
      ? state.items.filter(item => item.category === 'task' && !item.resolvedAt).length
      : state.counts.task,
    resultCount: state => state.previewIdentity
      ? state.items.filter(item => item.category === 'result').length
      : state.counts.result,
  },
  actions: {
    async fetchNotifications(options: {
      reset?: boolean;
      unread?: boolean;
      category?: NotificationCategory;
    } = {}) {
      if (!localStorage.getItem('token') || this.previewIdentity)
        return;
      const reset = options.reset ?? true;
      const category = options.category ?? (reset ? undefined : this.currentCategory);
      const unread = options.unread ?? (reset ? false : this.currentUnreadOnly);
      if (!reset && !this.hasMore)
        return;
      const requestId = ++this.requestSequence;
      this.loading = true;
      this.error = '';
      try {
        const targetPage = reset ? 1 : this.page + 1;
        const response = await fetchNotificationsApi({
          page: targetPage,
          page_size: 20,
          category,
          unread,
        });
        if (requestId !== this.requestSequence)
          return;
        const mapped = response.notifications
          .map(mapNotification)
          .filter((item): item is NotificationItem => item !== null);
        if (reset) {
          this.items = mapped;
        }
        else {
          const existingIds = new Set(this.items.map(item => item.id));
          this.items.push(...mapped.filter(item => !existingIds.has(item.id)));
        }
        this.page = response.pagination.page;
        this.hasMore = response.pagination.has_more;
        this.total = response.pagination.total;
        this.counts = response.counts;
        this.currentCategory = category;
        this.currentUnreadOnly = unread;
        this.lastLoadedAt = Date.now();
      }
      catch (exception: any) {
        if (requestId === this.requestSequence)
          this.error = exception?.message || '通知加载失败';
      }
      finally {
        if (requestId === this.requestSequence)
          this.loading = false;
      }
    },
    loadPreview(role: number, isAdmin: boolean, enabled: boolean) {
      if (!enabled) {
        if (this.previewIdentity) {
          this.items = [];
          this.previewIdentity = '';
        }
        return;
      }

      const identity = `${role}:${isAdmin ? 'admin' : 'member'}`;
      if (this.previewIdentity === identity && this.items.length > 0)
        return;

      this.items = previewItems(role, isAdmin);
      this.previewIdentity = identity;
    },
    async markAsRead(id: number | string) {
      const item = this.items.find(notification => notification.id === id);
      if (!item || item.isRead)
        return;
      item.isRead = true;
      if (this.previewIdentity)
        return;
      try {
        await markNotificationReadApi(id);
        this.counts.unread = Math.max(0, this.counts.unread - 1);
      }
      catch (exception) {
        item.isRead = false;
        throw exception;
      }
    },
    async markAllAsRead() {
      const unread = this.items.filter(item => !item.isRead);
      this.items.forEach((item) => {
        item.isRead = true;
      });
      if (this.previewIdentity)
        return;
      try {
        await markAllNotificationsReadApi();
        this.counts.unread = 0;
      }
      catch (exception) {
        unread.forEach(item => item.isRead = false);
        throw exception;
      }
    },
    async archive(id: number | string) {
      const index = this.items.findIndex(item => item.id === id);
      if (index < 0)
        return;
      const [item] = this.items.splice(index, 1);
      if (this.previewIdentity)
        return;
      try {
        await archiveNotificationApi(id);
        this.total = Math.max(0, this.total - 1);
        await this.fetchNotifications({
          reset: true,
          category: this.currentCategory,
          unread: this.currentUnreadOnly,
        });
      }
      catch (exception) {
        this.items.splice(index, 0, item);
        throw exception;
      }
    },
    async archiveRead() {
      if (this.previewIdentity) {
        this.items = this.items.filter(item => !item.isRead);
        return;
      }
      await archiveReadNotificationsApi();
      await this.fetchNotifications({
        reset: true,
        category: this.currentCategory,
        unread: this.currentUnreadOnly,
      });
    },
  },
});
